import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Determining whether the browser is on a mobile device or not
const isMobile = window.matchMedia('(pointer: coarse)').matches;

// Utility to get the viewport diagonal
function useViewportDiagonal() {
  const [diagonal, setDiagonal] = useState(() => {
    return Math.ceil(Math.sqrt(window.innerWidth ** 2 + window.innerHeight ** 2));
  });
  useEffect(() => {
    const handleResize = () => {
      setDiagonal(Math.ceil(Math.sqrt(window.innerWidth ** 2 + window.innerHeight ** 2)));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return diagonal;
}

function SpiralGalaxy({ size = 80, rotation = 0 }) {
  return (
    <svg
      width={size}
      height={size}
      style={{
        position: 'absolute',
        transform: `rotate(${rotation}deg)`,
        pointerEvents: 'none'
      }}
      viewBox="0 0 100 100"
    >
      <g>
        {[...Array(4)].map((_, arm) => (
          <path
            key={arm}
            d={`
              M 50 50
              ${Array.from({ length: 40 }).map((_, i) => {
                const angle = (i / 40) * 2 * Math.PI * 2 + (arm * Math.PI / 2);
                const radius = 5 + i * 1.5;
                const x = 50 + Math.cos(angle) * radius;
                const y = 50 + Math.sin(angle) * radius;
                return `L ${x} ${y}`;
              }).join(' ')}
            `}
            stroke="#fff"
            strokeWidth={1.5}
            fill="none"
            opacity={0.6}
          />
        ))}
        <circle cx="50" cy="50" r="6" fill="#c2aaff" opacity={0.7} />
      </g>
    </svg>
  );
}

// Generate objects in a circle of given diameter, centered at (cx, cy)
function generateSpaceObjects(diameter) {
  const radius = diameter / 2;
  const cx = radius;
  const cy = radius;

  // Helper to get a random point inside the circle
  function randomPointInCircle() {
    const t = 2 * Math.PI * Math.random();
    const r = radius * Math.sqrt(Math.random());
    return {
      x: cx + r * Math.cos(t),
      y: cy + r * Math.sin(t)
    };
  }

  // Stars
  const stars = Array.from({ length: 1000 }).map(() => {
    const { x, y } = randomPointInCircle();
    return {
      x, y,
      size: Math.random() * 2 + 1
    };
  });

  // Planets
  const planets = Array.from({ length: 10 }).map((_, i) => {
    const { x, y } = randomPointInCircle();
    return {
      x, y,
      size: 20 + Math.random() * 20,
      isBlue: i % 2 === 0,
      baseSlant: Math.random() * 40 - 20
    };
  });

  // Galaxies
  const galaxies = Array.from({ length: 3 }).map(() => {
    const { x, y } = randomPointInCircle();
    return {
      x, y,
      size: 60 + Math.random() * 40,
      rotation: Math.random() * 360
    };
  });

  return { stars, planets, galaxies };
}

function Home() {
  const navigate = useNavigate();
  const [mode, setMode] = useState(1); // default to dark mode :)
  const [backgroundRotation, setBackgroundRotation] = useState(0);
  const [ringRotation, setRingRotation] = useState(0);
  const [toggleCount, setToggleCount] = useState(0);
  const [lastToggleTime, setLastToggleTime] = useState(null);
  const [clickCount, setClickCount] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  // Get the diagonal of the viewport (for the background diameter)
  const diagonal = useViewportDiagonal();
  // Only generate objects once per mount or diagonal change
  const spaceObjectsRef = useRef(generateSpaceObjects(diagonal * 1.1)); // 10% larger for margin
  useEffect(() => {
    spaceObjectsRef.current = generateSpaceObjects(diagonal * 1.1);
  }, [diagonal]);

  // Track viewport size
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Preload images for faster display
  useEffect(() => {
    const imagesToPreload = [
      '/blog-preview.jpg',
      '/lyrics-preview.png',
      '/flappy-bird-preview.png',
      '/in-the-wrong-preview.svg'
    ];
    
    imagesToPreload.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Space mode animations
  useEffect(() => {
    if (mode === 2) {
      const bgInterval = setInterval(() => {
        setBackgroundRotation(prev => (prev + 0.05) % 360); // 0.1 degree per second
      }, 50); // Update every 50ms
      const ringInterval = setInterval(() => {
        setRingRotation(prev => (prev + 1) % 360); // Rings spin faster
      }, 50);
      return () => {
        clearInterval(bgInterval);
        clearInterval(ringInterval);
      };
    }
  }, [mode]);

  // Toggle logic (10 clicks detection)
  const handleToggle = () => {
    setClickCount(prev => prev + 1);
    const now = Date.now();
    if (lastToggleTime && now - lastToggleTime < 2000) {
      setToggleCount(toggleCount + 1);
    } else {
      setToggleCount(1);
    }
    setLastToggleTime(now);
    setMode(mode === 0 ? 1 : 0);
    if (toggleCount + 1 >= 10 && now - lastToggleTime < 2000) {
      setMode(2);
      setClickCount(0);
    }
  };

  // Reset toggle count after 2s
  useEffect(() => {
    const timer = setTimeout(() => setToggleCount(0), 2000);
    return () => clearTimeout(timer);
  }, [lastToggleTime]);

  // Reset click count after 300ms
  useEffect(() => {
    if (clickCount > 0) {
      const timer = setTimeout(() => setClickCount(0), 300);
      return () => clearTimeout(timer);
    }
  }, [clickCount]);

  const sliderBg = [
    '#e0e0e0',
    'linear-gradient(90deg, #2196f3 0%, #1976d2 100%)',
    'linear-gradient(90deg, #a259ff 0%, #6a0572 100%)'
  ];

   // Mobile needs a bit more to the left or something like that
  const sliderCirclePos = mode === 0 ? 2 : (isMobile ? 28 : 30);
  const textColors = ['black', 'white', 'white'];

  // For centering the rotating background
  const bgDiameter = diagonal * 1.1;

  const { stars, planets, galaxies } = spaceObjectsRef.current;

  // Calculate button size based on viewport
  const buttonWidth = Math.max(200, Math.min(300, viewportWidth * 0.3));
  const buttonHeight = buttonWidth * (160 / 260); // Maintain aspect ratio

  // Decide layout: side-by-side if enough width, otherwise stacked
  const isHorizontalLayout = viewportWidth > buttonWidth * 2.8; // Enough for two buttons + margin

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      width: '100vw',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: mode === 2 ? '#000' : mode === 1 ? '#191a1a' : '#faf9f6',
      color: mode === 2 ? 'white' : mode === 1 ? 'white' : 'black',
      overflowX: 'hidden'
    }}>
      {/* Space background (rotating, generated, always covers viewport) */}
      {mode === 2 && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          width: `${bgDiameter}px`,
          height: `${bgDiameter}px`,
          transformOrigin: 'center center',
          transform: `translate(-50%, -50%) rotate(${backgroundRotation}deg)`,
          zIndex: 0,
          pointerEvents: 'none'
        }}>
          {/* Stars */}
          {stars.map((star, i) => (
            <div
              key={`star-${i}`}
              style={{
                position: 'absolute',
                top: `${star.y}px`,
                left: `${star.x}px`,
                width: `${star.size}px`,
                height: `${star.size}px`,
                background: 'white',
                borderRadius: '50%'
              }}
            />
          ))}
          {/* Planets with rotating rings */}
          {planets.map((p, i) => (
            <div
              key={`planet-${i}`}
              style={{
                position: 'absolute',
                top: `${p.y}px`,
                left: `${p.x}px`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                background: p.isBlue
                  ? 'radial-gradient(circle, #00b4d8 60%, #0077b6 100%)'
                  : 'radial-gradient(circle, #ffb47b 60%, #ff7b25 100%)',
                borderRadius: '50%'
              }}
            >
              {/* Ring (rotates continuously) */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: `translate(-50%, -50%) rotate(${p.baseSlant + ringRotation}deg)`,
                  width: `${p.size * 1.5}px`,
                  height: `${p.size * 0.45}px`,
                  border: '2px solid rgba(255,255,255,0.5)',
                  borderRadius: '50%',
                  background: 'transparent',
                  clipPath: 'polygon(0% 0%, 100% 0%, 100% 50%, 0% 50%)'
                }}
              />
            </div>
          ))}
          {/* Galaxies */}
          {galaxies.map((g, i) => (
            <div
              key={`galaxy-${i}`}
              style={{
                position: 'absolute',
                top: `${g.y}px`,
                left: `${g.x}px`,
                width: `${g.size}px`,
                height: `${g.size}px`,
                pointerEvents: 'none'
              }}
            >
              <SpiralGalaxy size={g.size} rotation={g.rotation} />
            </div>
          ))}
        </div>
      )}

      {/* All your UI elements (buttons, text, etc.) */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Slider Toggle */}
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            width: '120px',
            height: '36px',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            userSelect: 'none'
          }}
        >
          <span style={{ fontSize: '20px', marginRight: '8px', opacity: mode === 0 ? 1 : 0.5 }}>🌞</span>
          <div
            onClick={handleToggle}
            style={{
              width: '56px',
              height: '28px',
              borderRadius: '14px',
              background: sliderBg[mode],
              position: 'relative',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
              transition: 'background 0.3s',
              display: 'flex',
              alignItems: 'center',
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            {/* The white circle */}
            <div
              style={{
                position: 'absolute',
                top: '2px',
                left: `${sliderCirclePos}px`,
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                transition: 'left 0.3s'
              }}
            />
          </div>
          <span style={{ fontSize: '20px', marginLeft: '8px', opacity: mode === 1 || mode === 2 ? 1 : 0.5 }}>
            {mode === 2 ? '🚀' : '🌙'}
          </span>
        </div>

        {/* Main content - Title and subtitle */}
        <div style={{ textAlign: 'center', marginTop: '60px', position: 'relative', zIndex: 1 }}>
          <h1 style={{
            fontSize: '60pt',
            fontFamily: "'Fredoka One', cursive",
            textTransform: 'lowercase',
            color: textColors[mode],
            marginBottom: '20px',
            fontWeight: 'normal'
          }}>
            reilly.fun
          </h1>
          <h2 style={{
            fontSize: '24pt',
            //fontFamily: "'Fredoka One', cursive",
            fontWeight: 'normal',
            color: textColors[mode],
            textShadow: mode === 2 ? '0 2px 8px #111' : undefined,
            marginTop: '0',
            marginBottom: '80px'
          }}>
            Little creations
          </h2>
        </div>

        {/* APP BUTTONS */}
        <div style={{ 
          display: 'flex', 
          flexDirection: isHorizontalLayout ? 'row' : 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: '40px',
          marginTop: '20px',
          position: 'relative',
          zIndex: 1
        }}>
          {/* GasMeUp Button */} {/*
          <button
            draggable={true}
            onDragStart={e => {
              // Set the drag data to your app's URL
              e.dataTransfer.setData('text/uri-list', window.location.origin + '/gasmeup');
            }}
            onClick={() => navigate('/gasmeup')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              outline: 'none',
              borderRadius: '24px',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)',
              width: buttonWidth,
              height: buttonHeight
            }}
            onMouseOver={e => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 8px 24px rgba(0,0,0,0.2), 0 1.5px 3px rgba(0,0,0,0.15)' : '0 8px 24px rgba(255,255,255,0.2), 0 1.5px 3px rgba(255,255,255,0.15)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)';
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '24px',
                overflow: 'hidden',
                position: 'relative',
                background: mode === 0 ? '#e0e0e0' : '#333'
              }}
            >
              <img
                src="/gasmeup-preview.png"
                alt="GasMeUp Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: mode === 0 ? 'black' : 'white',
                  fontFamily: "'Fredoka One', cursive",
                  fontSize: Math.min(buttonWidth * 0.12, 32),
                  fontWeight: 'bold',
                  textShadow: mode === 0 ? '1px 1px 2px rgba(255,255,255,0.8)' : '1px 1px 2px rgba(0,0,0,0.8)',
                  whiteSpace: 'nowrap'
                }}
              >
                GasMeUp
              </div>
            </div>
          </button> */}

          {/* Betting Button */} {/*
          <button
            draggable={true}
            onDragStart={e => {
              // Set the drag data to your app's URL
              e.dataTransfer.setData('text/uri-list', window.location.origin + '/betting');
            }}
            onClick={() => navigate('/betting')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              outline: 'none',
              borderRadius: '24px',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)',
              width: buttonWidth,
              height: buttonHeight
            }}
            onMouseOver={e => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 8px 24px rgba(0,0,0,0.2), 0 1.5px 3px rgba(0,0,0,0.15)' : '0 8px 24px rgba(255,255,255,0.2), 0 1.5px 3px rgba(255,255,255,0.15)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)';
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '24px',
                overflow: 'hidden',
                position: 'relative',
                background: mode === 0 ? '#e0e0e0' : '#333'
              }}
            >
              <img
                src="/betting-preview.png"
                alt="Betting Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: mode === 0 ? 'black' : 'white',
                  fontFamily: "'Fredoka One', cursive",
                  fontSize: Math.min(buttonWidth * 0.12, 32),
                  fontWeight: 'bold',
                  textShadow: mode === 0 ? '1px 1px 2px rgba(255,255,255,0.8)' : '1px 1px 2px rgba(0,0,0,0.8)',
                  whiteSpace: 'nowrap'
                }}
              >
                Betting
              </div>
            </div>
          </button> */}

          {/* Timeline Button */}
          {/* <button
            draggable={true}
            onDragStart={e => {
              // Set the drag data to your app's URL
              e.dataTransfer.setData('text/uri-list', window.location.origin + '/timeline');
            }}
            onClick={() => navigate('/timeline')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              outline: 'none',
              borderRadius: '24px',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)',
              width: buttonWidth,
              height: buttonHeight
            }}
            onMouseOver={e => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 8px 24px rgba(0,0,0,0.2), 0 1.5px 3px rgba(0,0,0,0.15)' : '0 8px 24px rgba(255,255,255,0.2), 0 1.5px 3px rgba(255,255,255,0.15)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)';
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '24px',
                overflow: 'hidden',
                position: 'relative',
                background: mode === 0 ? '#e0e0e0' : '#333'
              }}
            >
              <img
                src="/timeline-preview.jpg"
                alt="Timeline Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: 'white',
                  fontFamily: "'Fredoka One', cursive",
                  fontSize: Math.min(buttonWidth * 0.12, 32),
                  fontWeight: 'bold',
                  textShadow: mode === 0 ? '1px 1px 2px rgba(255,255,255,0.8)' : '1px 1px 2px rgba(0,0,0,0.8)',
                  whiteSpace: 'nowrap'
                }}
              >
                Timeline
              </div>
            </div>
          </button> */}

          {/* Blog Button */}
          <button
            draggable={true}
            onDragStart={e => {
              // Set the drag data to your app's URL
              e.dataTransfer.setData('text/uri-list', window.location.origin + '/blog');
            }}
            onClick={() => navigate('/blog')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              outline: 'none',
              borderRadius: '24px',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)',
              width: buttonWidth,
              height: buttonHeight
            }}
            onMouseOver={e => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 8px 24px rgba(0,0,0,0.2), 0 1.5px 3px rgba(0,0,0,0.15)' : '0 8px 24px rgba(255,255,255,0.2), 0 1.5px 3px rgba(255,255,255,0.15)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)';
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '24px',
                overflow: 'hidden',
                position: 'relative',
                background: mode === 0 ? '#e0e0e0' : '#333'
              }}
            >
              <img
                src="/blog-preview.jpg"
                alt="Blog Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
              {/* <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: 'white',
                  fontFamily: "'Sniglet', cursive",
                  fontSize: Math.min(buttonWidth * 0.1, 30),
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap'
                }}
              >
                <span
                  className="alt-outlined-text"
                  style={{
                    fontWeight: 'bold',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                  }}
                >
                  <big><big>LyricaL</big></big>
                </span>
                <br /><i>(the game)</i>
              </div> */}
            </div>
          </button>

          {/* In The Wrong Button */}
          {/* <button
            draggable={true}
            onDragStart={e => {
              e.dataTransfer.setData('text/uri-list', window.location.origin + '/in-the-wrong');
            }}
            onClick={() => navigate('/in-the-wrong')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              outline: 'none',
              borderRadius: '24px',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)',
              width: buttonWidth,
              height: buttonHeight
            }}
            onMouseOver={e => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 8px 24px rgba(0,0,0,0.2), 0 1.5px 3px rgba(0,0,0,0.15)' : '0 8px 24px rgba(255,255,255,0.2), 0 1.5px 3px rgba(255,255,255,0.15)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)';
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '24px',
                overflow: 'hidden',
                position: 'relative',
                background: mode === 0 ? '#e0e0e0' : '#0d0d1a'
              }}
            >
              <img
                src="/in-the-wrong-preview.svg"
                alt="In The Wrong Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            </div>
          </button> */}

          {/* LyricaL Button */}
          <button
            draggable={true}
            onDragStart={e => {
              // Set the drag data to your app's URL
              e.dataTransfer.setData('text/uri-list', window.location.origin + '/lyrical');
            }}
            onClick={() => navigate('/lyrical')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              outline: 'none',
              borderRadius: '24px',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)',
              width: buttonWidth,
              height: buttonHeight
            }}
            onMouseOver={e => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 8px 24px rgba(0,0,0,0.2), 0 1.5px 3px rgba(0,0,0,0.15)' : '0 8px 24px rgba(255,255,255,0.2), 0 1.5px 3px rgba(255,255,255,0.15)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)';
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '24px',
                overflow: 'hidden',
                position: 'relative',
                background: mode === 0 ? '#e0e0e0' : '#333'
              }}
            >
              <img
                src="/lyrics-preview.png"
                alt="LyricaL Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: 'white',
                  fontFamily: "'Sniglet', cursive",
                  fontSize: Math.min(buttonWidth * 0.1, 30),
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap'
                }}
              >
                <span
                  className="alt-outlined-text"
                  style={{
                    fontWeight: 'bold',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                  }}
                >
                  <big><big>LyricaL</big></big>
                </span>
                <br /><i>(the game)</i>
              </div>
            </div>
          </button>

          {/* Flappy Bird Button */}
          <button
            draggable={true}
            onDragStart={e => {
              // Set the drag data to your app's URL
              e.dataTransfer.setData('text/uri-list', window.location.origin + '/flappy-bird');
            }}
            onClick={() => navigate('/flappy-bird')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              outline: 'none',
              borderRadius: '24px',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)',
              width: buttonWidth,
              height: buttonHeight
            }}
            onMouseOver={e => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 8px 24px rgba(0,0,0,0.2), 0 1.5px 3px rgba(0,0,0,0.15)' : '0 8px 24px rgba(255,255,255,0.2), 0 1.5px 3px rgba(255,255,255,0.15)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = mode === 0 ? '0 2px 8px rgba(0,0,0,0.10)' : '0 2px 8px rgba(255,255,255,0.10)';
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '24px',
                overflow: 'hidden',
                position: 'relative',
                background: mode === 0 ? '#e0e0e0' : '#333'
              }}
            >
              <img
                src="/flappy-bird-preview.png"
                alt="Flappy Bird Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: '#333',
                  fontFamily: "'Sniglet', cursive",
                  fontSize: Math.min(buttonWidth * 0.1, 32),
                  fontWeight: 400,
                  //textShadow: mode === 0 ? '1px 1px 2px rgba(255,255,255,0.8)' : '1px 1px 2px rgba(0,0,0,0.8)',
                  whiteSpace: 'nowrap'
                }}
              >
                Flappy Bird
              </div>
            </div>
          </button>

        </div>

        <div style={{ height: '100px' }} />

        {/* Space Mode Text */}
        {mode === 2 && (
          <div style={{
            position: 'fixed',
            bottom: '50px',
            right: '20px',
            fontFamily: "'Fredoka One', cursive",
            fontWeight: 'normal',
            fontSize: '14px',
            color: 'white',
            zIndex: 1000,
            textAlign: 'right'
          }}>
            {isMobile ? (
              <>
                You found space mode! <br />
                Tap the toggle again after 2 seconds to exit.
              </>
            ) : (
              <>
                You found space mode! Drag the corner <br />
                of your browser to approach lightspeed :) <br />
                <br />
                Tap the toggle again after 2 seconds to exit.
              </>
            )}
          </div>
        )}

        {/* Conditional footer */}
        {mode !== 2 && (
          <footer style={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            textAlign: 'center',
            padding: '10px 0',
            fontSize: '12px',
            backgroundColor: mode === 0 ? '#f0f0f0' : '#222',
            color: mode === 0 ? 'black' : 'white',
            zIndex: 2
          }}>
            made by rx – <a href="https://www.linkedin.com/in/reilly-xu/" target="_blank" rel="noopener noreferrer" style={{ color: mode === 0 ? 'black' : 'white' }}>about me</a>
          </footer>
        )}
        {mode === 2 && (
          <footer style={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            textAlign: 'center',
            padding: '10px 0',
            fontSize: '12px',
            backgroundColor: mode === 0 ? '#f0f0f0' : '#222',
            color: mode === 0 ? 'black' : 'white',
            zIndex: 2
          }}>
            if you enjoyed, you can <a href="https://buymeacoffee.com/reillyfun" target="_blank" rel="noopener noreferrer" style={{ color: mode === 0 ? 'black' : 'white' }}>buy me a coffee!</a>
          </footer>
        )}
      </div>
    </div>
  );
}

export default Home;
